import { OrderDocument, OrderItemDocument } from '@models/order';
import { FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import { logError } from '@util/logError';
import { LabelResponse } from '../labels/labels.service';
import {
  addNewNote,
  markAsDelivered,
  updateOrderItemState,
} from '../orders/orders.service';
import {
  getEstimatesArgs,
  getLabelArgs,
  getRatesArgs,
  Rate,
  RateResponse,
  TrackPackageResponse,
} from './shipengine.types';
import { ProductDocument } from 'models/product';
import { AddressDocument } from 'models/address';

export async function getEstimates(args: getEstimatesArgs): Promise<Rate[]> {
  try {
    const { data } = await httpsCallable<getEstimatesArgs, Rate[]>(
      functions,
      FirebaseCallable.getEstimates
    )(args);
    return data;
  } catch (error) {
    logError(error);
    return [];
  }
}

export interface TrackPackageArgs {
  carrier: string;
  trackingNo: string;
  service_code?: string;
}

export async function trackPackage(
  args: TrackPackageArgs
): Promise<TrackPackageResponse> {
  const { data } = await httpsCallable<
    { carrier: string; trackingNo: string; service_code?: string },
    TrackPackageResponse
  >(
    functions,
    FirebaseCallable.trackPackage
  )(args);
  return data;
}

// returns tracking number -> delivery date
export async function trackPackages(
  args: TrackPackageArgs[]
): Promise<{ [key: string]: string }> {
  const { data } = await httpsCallable<
    TrackPackageArgs[],
    { [key: string]: string }
  >(
    functions,
    'v2_trackPackages'
  )(args);
  return data;
}

export async function trackPackageGlobal(
  trackingNo: string
): Promise<TrackPackageResponse> {
  const { data } = await httpsCallable<
    { trackingNo: string },
    TrackPackageResponse
  >(
    functions,
    FirebaseCallable.trackPackageGlobal
  )({ trackingNo });
  return data;
}

export async function getRates(args: getRatesArgs): Promise<Rate[]> {
  if (args.shipment?.package.insured_value?.amount === 0) {
    delete args.shipment.package.insured_value;
  }
  const { data } = await httpsCallable<getRatesArgs, RateResponse>(
    functions,
    FirebaseCallable.getRates
  )(args);

  if (data?.rate_response?.errors && !data?.rate_response?.rates?.length)
    throw new Error(
      data.rate_response.errors[0]?.message ??
        'Shipping is not available for this address. Please contact the seller for assistance.'
    );
  return data?.rate_response?.rates ?? [];
}

export async function fixLabels(orderId: string) {
  const { data } = await httpsCallable<
    { orderId: string },
    { updated: number; errors: string[] }
  >(
    functions,
    FirebaseCallable.fixLabel
  )({ orderId });
  return data;
}

export async function getCheapestEstimate(
  product_id: string,
  to_postal_code: string,
  to_country_code = 'US'
) {
  const rates = await getEstimates({
    product_id,
    to_postal_code,
    to_country_code,
  });

  let cheapest: number | null = null;
  rates.forEach((rate) => {
    cheapest = cheapest
      ? Math.min(cheapest, rate.total_amount)
      : rate.total_amount;
  });
  if (cheapest === null) {
    throw new Error(
      `Shipping is not available for this postal code and country: ${to_postal_code} ${to_country_code}`
    );
  }
  return cheapest;
}

export const checkDelivery = async (
  trackingResponse: TrackPackageResponse,
  order: OrderDocument,
  item: OrderItemDocument,
  isAdmin: boolean,
  skipAdminAlert?: boolean
) => {
  if (!trackingResponse || !order.sellers) return;
  try {
    if (
      trackingResponse.status_code === 'DE' &&
      item.state <= 1 &&
      trackingResponse.tracking_number
    ) {
      const orderPostal = order.address?.postal_code
        ?.split('-')?.[0]
        ?.replace(/\s+/g, '')
        ?.toUpperCase();
      const trackingPostal = trackingResponse.events
        ?.find((event) => event.postal_code)
        ?.postal_code?.split('-')?.[0]
        ?.replace(/\s+/g, '')
        ?.toUpperCase();

      if (orderPostal && trackingPostal && orderPostal === trackingPostal) {
        let foundTracking = false;
        Object.keys(order.sellers).find((sellerId) => {
          order.sellers![sellerId].forEach((item) => {
            if (item.tracking_number)
              item.tracking_number = item.tracking_number.toUpperCase();
            if (
              item.tracking_number ===
              trackingResponse.tracking_number.toUpperCase()
            ) {
              const milliToHour = 2.7777777777778e-7;
              const delivery_time =
                new Date(
                  trackingResponse.actual_delivery_date || 0
                ).valueOf() || Date.now();
              if ((delivery_time - order.created) * milliToHour > 12) {
                item.state = item.state < 2 ? 2 : item.state;
                item.delivery_time = delivery_time;
                foundTracking = true;
              } else {
                if (!skipAdminAlert && isAdmin)
                  alert(
                    `Delivery Time (${delivery_time}) isn't 12hrs after Order Creation (${order.created})`
                  );
                return;
              }
            }
          });
          return foundTracking;
        });
        if (foundTracking) {
          await markAsDelivered(order);
          if (!skipAdminAlert && isAdmin) {
            await addNewNote(
              order.id,
              'Delivery Confirmed from Modal',
              process.env.NEXT_PUBLIC_SUPPORT_ID
            );
            alert('Order status updated to delivered');
          }
        }
      } else if (!skipAdminAlert && isAdmin) {
        alert(
          `Order Postal (${orderPostal}) doesn't match Tracking Postal (${trackingPostal})`
        );
      }
    } else if (
      trackingResponse.status_code === 'IT' &&
      item.state === 0 &&
      item.tracking_number === trackingResponse.tracking_number &&
      isAdmin
    ) {
      await updateOrderItemState(order, item.seller_id, item.id, 1);
      await addNewNote(
        order.id,
        'Item Updated To In Transit from modal',
        process.env.NEXT_PUBLIC_SUPPORT_ID
      );
    }
  } catch (e) {
    logError(e);
    if (!skipAdminAlert && isAdmin)
      alert('Error updating order, check console');
  }
};

export const purchaseLabel = async (args: getLabelArgs) => {
  return await httpsCallable<
    getLabelArgs,
    LabelResponse & { rate_id: string; charged: number }
  >(
    functions,
    FirebaseCallable.getLabel
  )(args);
};

// if label_id is unavailable, use tracking_number
export async function voidLabel(label_id: string, tn?: string) {
  const { data } = await httpsCallable<
    { label_id: string; tn?: string },
    { approved: boolean; message: string }
  >(
    functions,
    FirebaseCallable.voidLabel
  )({ label_id, ...(tn && { tn }) });
  return data;
}

export async function printReturnLabel(label_id: string) {
  const { data } = await httpsCallable<{ label_id: string }, LabelResponse>(
    functions,
    FirebaseCallable.getReturnLabel
  )({ label_id });
  return data;
}

export function postalCodeToState(postalCode: string) {
  // Remove non-numeric characters and pad to 5 digits
  const zip = parseInt(postalCode.replace(/\D/g, '').padStart(5, '0'), 10);
  if (isNaN(zip)) return undefined;

  const ranges = [
    { st: 'AK', min: 99501, max: 99950 },
    { st: 'AL', min: 35004, max: 36925 },
    { st: 'AR', min: 71601, max: 72959 },
    { st: 'AR', min: 75502, max: 75502 },
    { st: 'AZ', min: 85001, max: 86556 },
    { st: 'CA', min: 90001, max: 96162 },
    { st: 'CO', min: 80001, max: 81658 },
    { st: 'CT', min: 6001, max: 6389 },
    { st: 'CT', min: 6401, max: 6928 },
    { st: 'DC', min: 20001, max: 20039 },
    { st: 'DC', min: 20042, max: 20599 },
    { st: 'DC', min: 20799, max: 20799 },
    { st: 'DE', min: 19701, max: 19980 },
    { st: 'FL', min: 32004, max: 34997 },
    { st: 'GA', min: 30001, max: 31999 },
    { st: 'GA', min: 39901, max: 39901 },
    { st: 'HI', min: 96701, max: 96898 },
    { st: 'IA', min: 50001, max: 52809 },
    { st: 'IA', min: 68119, max: 68120 },
    { st: 'ID', min: 83201, max: 83876 },
    { st: 'IL', min: 60001, max: 62999 },
    { st: 'IN', min: 46001, max: 47997 },
    { st: 'KS', min: 66002, max: 67954 },
    { st: 'KY', min: 40003, max: 42788 },
    { st: 'LA', min: 70001, max: 71232 },
    { st: 'LA', min: 71234, max: 71497 },
    { st: 'MA', min: 1001, max: 2791 },
    { st: 'MA', min: 5501, max: 5544 },
    { st: 'MD', min: 20331, max: 20331 },
    { st: 'MD', min: 20335, max: 20797 },
    { st: 'MD', min: 20812, max: 21930 },
    { st: 'ME', min: 3901, max: 4992 },
    { st: 'MI', min: 48001, max: 49971 },
    { st: 'MN', min: 55001, max: 56763 },
    { st: 'MO', min: 63001, max: 65899 },
    { st: 'MS', min: 38601, max: 39776 },
    { st: 'MS', min: 71233, max: 71233 },
    { st: 'MT', min: 59001, max: 59937 },
    { st: 'NC', min: 27006, max: 28909 },
    { st: 'ND', min: 58001, max: 58856 },
    { st: 'NE', min: 68001, max: 68118 },
    { st: 'NE', min: 68122, max: 69367 },
    { st: 'NH', min: 3031, max: 3897 },
    { st: 'NJ', min: 7001, max: 8989 },
    { st: 'NM', min: 87001, max: 88441 },
    { st: 'NV', min: 88901, max: 89883 },
    { st: 'NY', min: 6390, max: 6390 },
    { st: 'NY', min: 10001, max: 14975 },
    { st: 'OH', min: 43001, max: 45999 },
    { st: 'OK', min: 73001, max: 73199 },
    { st: 'OK', min: 73401, max: 74966 },
    { st: 'OR', min: 97001, max: 97920 },
    { st: 'PA', min: 15001, max: 19640 },
    { st: 'PR', min: 0, max: 0 },
    { st: 'RI', min: 2801, max: 2940 },
    { st: 'SC', min: 29001, max: 29948 },
    { st: 'SD', min: 57001, max: 57799 },
    { st: 'TN', min: 37010, max: 38589 },
    { st: 'TX', min: 73301, max: 73301 },
    { st: 'TX', min: 75001, max: 75501 },
    { st: 'TX', min: 75503, max: 79999 },
    { st: 'TX', min: 88510, max: 88589 },
    { st: 'UT', min: 84001, max: 84784 },
    { st: 'VA', min: 20040, max: 20041 },
    { st: 'VA', min: 20040, max: 20167 },
    { st: 'VA', min: 20042, max: 20042 },
    { st: 'VA', min: 22001, max: 24658 },
    { st: 'VT', min: 5001, max: 5495 },
    { st: 'VT', min: 5601, max: 5907 },
    { st: 'WA', min: 98001, max: 99403 },
    { st: 'WI', min: 53001, max: 54990 },
    { st: 'WV', min: 24701, max: 26886 },
    { st: 'WY', min: 82001, max: 83128 },
  ];

  for (const range of ranges) {
    if (zip >= range.min && zip <= range.max) {
      return range.st;
    }
  }
  return undefined;
}

export function isDomestic(
  shipment: ProductDocument['shipment'],
  shipTo: Pick<AddressDocument, 'state_province' | 'country_code'>
): shipment is NonNullable<ProductDocument['shipment']> & {
  domestic_flat_rate: number;
} {
  if (shipment?.domestic_flat_rate === undefined) return false;
  if (['AK', 'HI', 'PR'].includes(shipTo.state_province ?? ''))
    return shipment?.ship_from?.state_province === shipTo.state_province;
  return shipTo.country_code === shipment?.ship_from?.country_code;
}

export function shouldExcludeCountry(
  shipment: ProductDocument['shipment'],
  shipTo: AddressDocument
) {
  if (!shipment?.excluded_countries) return false;
  return (
    shipment?.excluded_countries?.includes(shipTo.country_code) ||
    (['AK', 'HI', 'PR'].includes(shipTo.state_province ?? '') &&
      shipment?.excluded_countries?.includes(shipTo.state_province ?? ''))
  );
}
